import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { pathwayErrorCode, pathwayErrorMessage } from "../../pathways/errors";
import { resolveApiUrl } from "../../pathways/learnerApi";
import ModalShell from "./ModalShell";
import { fmtDaysAgo } from "./format";

export const FEEDBACK_MESSAGE_MAX = 2000;

/** Refusals that mean "the queue on screen is stale": the screen re-reads it. */
const STALE_CODES = new Set(["SUBMISSION_ALREADY_REVIEWED", "SUBMISSION_NOT_FOUND"]);

/**
 * The Review queue of the dashboard (spec screen 5, contract §10.3, §10.11): the practice
 * submissions waiting for the practitioner, oldest first, as in the wireframe:
 *
 *   Review queue · 2
 *   Kavya · Reflection prompt                         3 days ago
 *   "Felt calmer after the third round…"
 *   [Approve]  [Send feedback]
 *   Priya · Reflection prompt  [photo]
 *
 * Approve sends nothing to the learner; Send feedback opens a message box and e-mails it
 * (the step stays done either way, contract §10.0.2). Each write re-reads the dashboard,
 * so the count in the title and the list are the server's, never patched here.
 *
 * @param {object} props
 * @param {number} props.pending      `PathwayDashboard.review_pending`
 * @param {object[]} props.items      the pending `ReviewItem`s
 * @param {(id: string) => Promise<void>} props.onApprove
 * @param {(id: string, message: string) => Promise<void>} props.onFeedback
 * @param {() => Promise<void>} props.onStale   re-read after a stale-queue refusal
 */
export default function ReviewQueue({ pending, items, onApprove, onFeedback, onStale }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [busy, setBusy] = useState(null);         // the id being approved
  const [replying, setReplying] = useState(null); // the ReviewItem the feedback box is for

  const approve = async (item) => {
    setBusy(item.id);
    try {
      await onApprove(item.id);
      toast.success(t("path.review.approved", { name: item.name || item.email }));
    } catch (e) {
      toast.error(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
      if (STALE_CODES.has(pathwayErrorCode(e))) await onStale();
    } finally {
      setBusy(null);
    }
  };

  const sent = (item) => {
    setReplying(null);
    toast.success(t("path.review.feedbackSent", { name: item.name || item.email }));
  };

  return (
    <section data-testid={IDS.reviewQueue} aria-labelledby="pathway-review-heading" className="bg-white rounded-3xl border border-eh-border-soft p-5 space-y-3">
      <h2 id="pathway-review-heading" data-testid={IDS.reviewTitle} className="text-sm font-semibold text-eh-ink">
        {t("path.review.title", { n: pending })}
      </h2>
      {items.length === 0 ? (
        <p data-testid={IDS.reviewEmpty} className="text-sm text-eh-muted">{t("path.review.empty")}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.id} data-testid={IDS.reviewItem(item.id)} className="rounded-2xl bg-eh-cream/60 border border-eh-border-soft px-4 py-3 space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-eh-ink">
                  {t("path.review.who", { name: item.name || item.email, step: item.step_title })}
                </p>
                <span className="text-xs text-eh-muted">{fmtDaysAgo(item.submitted_at, lang)}</span>
              </div>
              {item.text && (
                <blockquote data-testid={IDS.reviewText(item.id)} className="text-sm text-eh-ink whitespace-pre-line border-l-2 border-eh-border pl-3">
                  {t("path.review.quote", { text: item.text })}
                </blockquote>
              )}
              {item.photo && (
                <a
                  href={resolveApiUrl(item.photo.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={IDS.reviewPhoto(item.id)}
                  className="inline-block"
                >
                  <img
                    src={resolveApiUrl(item.photo.url)}
                    alt={t("path.review.photoAlt", { name: item.name || item.email })}
                    className="h-24 w-24 rounded-xl object-cover border border-eh-border-soft"
                  />
                </a>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => approve(item)}
                  disabled={busy !== null}
                  data-testid={IDS.reviewApprove(item.id)}
                  className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90"
                >
                  {busy === item.id
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                    : <Check className="w-4 h-4 mr-2" aria-hidden="true" />}
                  {t("path.review.approve")}
                </Button>
                <Button variant="outline" onClick={() => setReplying(item)} disabled={busy !== null} data-testid={IDS.reviewFeedback(item.id)}>
                  <MessageSquare className="w-4 h-4 mr-2" aria-hidden="true" /> {t("path.review.sendFeedback")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <FeedbackModal
        item={replying}
        onSend={onFeedback}
        onSent={sent}
        onStale={onStale}
        onClose={() => setReplying(null)}
      />
    </section>
  );
}

function FeedbackModal({ item, onSend, onSent, onStale, onClose }) {
  return (
    <ModalShell open={Boolean(item)} onClose={onClose} labelledBy="pathway-feedback-heading" testId={IDS.feedbackModal} panelClassName="max-w-xl">
      {item && <FeedbackForm key={item.id} item={item} onSend={onSend} onSent={onSent} onStale={onStale} onClose={onClose} />}
    </ModalShell>
  );
}

function FeedbackForm({ item, onSend, onSent, onStale, onClose }) {
  const t = useT("admin");
  const tc = useT("common");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const name = item.name || item.email;

  const send = async () => {
    const text = message.trim();
    if (!text) {
      setError(t("path.review.feedbackEmpty"));
      return;
    }
    setSending(true);
    setError("");
    try {
      await onSend(item.id, text);
      onSent(item);
    } catch (e) {
      setError(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
      if (STALE_CODES.has(pathwayErrorCode(e))) await onStale();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-5 sm:p-6 space-y-4">
      <h2 id="pathway-feedback-heading" className="font-display text-2xl text-eh-ink">{t("path.review.feedbackTitle", { name })}</h2>
      <p className="text-sm text-eh-muted">{t("path.review.feedbackHint", { step: item.step_title })}</p>
      <div>
        <label htmlFor="pathway-feedback-message" className="block text-sm text-eh-muted mb-1.5">{t("path.review.feedbackMessage")}</label>
        <textarea
          id="pathway-feedback-message"
          value={message}
          onChange={(e) => { setMessage(e.target.value); setError(""); }}
          rows={5}
          maxLength={FEEDBACK_MESSAGE_MAX}
          autoFocus
          data-testid={IDS.feedbackMessage}
          className="w-full border border-input rounded-md px-3 py-2 text-sm bg-white resize-y"
        />
        <FieldError message={error} testId={IDS.feedbackError} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>{t("path.editor.cancel")}</Button>
        <Button onClick={send} disabled={sending} data-testid={IDS.feedbackSend} className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90">
          {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
          {t("path.review.feedbackSend")}
        </Button>
      </div>
    </div>
  );
}
